import React from 'react';
import { Target, Shield, HelpCircle, Heart, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function BeginnerFriendly() {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <Link to="/about" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to About
        </Link>

        <div className="mb-12">
          <div className="inline-flex items-center gap-3 bg-primary/10 border border-primary/30 rounded-full px-6 py-3 mb-6">
            <Target className="w-6 h-6 text-primary" />
            <span className="text-primary font-semibold">Safe Learning</span>
          </div>
          <h1 className="text-5xl font-heading font-bold mb-6">Beginner Friendly</h1>
          <p className="text-xl text-muted-foreground">
            No question is too basic. We welcome absolute beginners and create a safe learning environment.
          </p>
        </div>

        <div className="prose prose-invert max-w-none">
          <div className="bg-card border border-border rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-3">
              <Shield className="w-6 h-6 text-primary" />
              A Safe Space to Learn
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Learning poker can be intimidating. Confusing terminology, complex strategy, and fear of looking 
              foolish can prevent people from ever starting. At Luck or Skills, we've eliminated those barriers.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Our community is built on the principle that everyone starts somewhere, and no question is too simple. 
              We celebrate beginners and create an environment where asking "basic" questions is not just acceptable—it's encouraged.
            </p>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-3">
              <HelpCircle className="w-6 h-6 text-primary" />
              What Makes Us Beginner-Friendly
            </h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold mb-2 text-primary">Zero Assumed Knowledge</h3>
                <p className="text-muted-foreground">
                  We start from the absolute beginning. Don't know what a "flop" is? Perfect. Never played a hand? 
                  Even better. Our foundational lessons assume you know nothing about poker.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-primary">Plain English Explanations</h3>
                <p className="text-muted-foreground">
                  We avoid jargon until you're ready for it, and when we do introduce poker terminology, we explain 
                  it clearly with real examples. No confusing lingo that leaves you scratching your head.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-primary">Patient Instructors</h3>
                <p className="text-muted-foreground">
                  Our panel members have taught hundreds of beginners. They remember what it's like to be new and 
                  take genuine joy in helping others learn. No condescension, no impatience—just helpful guidance.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2 text-primary">Judgment-Free Zone</h3>
                <p className="text-muted-foreground">
                  Made a mistake? That's how you learn. Asked the same question twice? No problem. Everyone in our 
                  community has been a beginner, and we all support each other's learning journey.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-heading font-bold mb-4 flex items-center gap-3">
              <Heart className="w-6 h-6 text-primary" />
              Common Beginner Concerns (And Why They Don't Apply Here)
            </h2>
            <div className="space-y-4">
              <div className="bg-background/50 rounded-lg p-4">
                <p className="font-semibold text-primary mb-2">"What if I ask a stupid question?"</p>
                <p className="text-muted-foreground text-sm">
                  There are no stupid questions here. Every expert player started as a confused beginner. 
                  Our community celebrates curiosity and encourages all questions.
                </p>
              </div>
              <div className="bg-background/50 rounded-lg p-4">
                <p className="font-semibold text-primary mb-2">"Everyone will be better than me."</p>
                <p className="text-muted-foreground text-sm">
                  We have members at all skill levels, from people who've never played a hand to experienced players. 
                  You'll find plenty of people at your level, and everyone is supportive.
                </p>
              </div>
              <div className="bg-background/50 rounded-lg p-4">
                <p className="font-semibold text-primary mb-2">"It's too complicated to learn."</p>
                <p className="text-muted-foreground text-sm">
                  We break everything down into bite-sized pieces. You'll learn one concept at a time, building 
                  confidence with each lesson. Before you know it, you'll be playing with confidence.
                </p>
              </div>
              <div className="bg-background/50 rounded-lg p-4">
                <p className="font-semibold text-primary mb-2">"I'm too old/young to start learning poker."</p>
                <p className="text-muted-foreground text-sm">
                  We have members from their 20s to their 70s. Poker is for everyone, and we welcome players 
                  of all ages who want to learn this timeless game.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-primary/10 border border-primary/30 rounded-2xl p-8">
            <h3 className="text-xl font-heading font-bold mb-3">Your First Steps</h3>
            <p className="text-muted-foreground mb-4">
              Starting your poker journey with Luck or Skills is easy:
            </p>
            <ol className="space-y-3 text-muted-foreground">
              <li><strong className="text-primary">1.</strong> Join the community and introduce yourself in the forum</li>
              <li><strong className="text-primary">2.</strong> Watch our "Absolute Beginner" lesson series</li>
              <li><strong className="text-primary">3.</strong> Attend a live session (don't worry, you can just watch!)</li>
              <li><strong className="text-primary">4.</strong> Ask questions—lots of them!</li>
              <li><strong className="text-primary">5.</strong> Practice hands at your own pace with AI feedback</li>
            </ol>
          </div>

          <div className="mt-12 text-center">
            <Link 
              to="/membership" 
              className="inline-flex items-center gap-3 bg-primary hover:bg-primary/90 text-black px-8 py-4 rounded-xl font-bold text-lg transition-all transform hover:scale-105"
            >
              Start Your Journey
              <Target className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}